// safety/safety-walks/pages/CloseOutObservationPage.tsx
// Close Out Observation (Miro: "Responsible Person or Safety Officer Close Obs").
// Corrective action + resolution notes + evidence-of-fix photos (mandatory,
// multiple, PNG/JPG ≤10MB) + fixed date. Submit -> Pending Verification;
// RP and Safety Manager are notified.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Loader2, Upload, X, ImageIcon, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { getObservation, submitCloseOut, uploadPhoto, removePhoto } from '../../../../utils/safety-walks-api';
import { RiskBadge, DueIndicator, fmtDate, PhotoThumb, PhotoLightbox } from '../components/shared';
import type { PhotoMeta, SafetyObservation } from '../../../../types/safety-walk.types';

export const CloseOutObservationPage: React.FC = () => {
    const { obsId } = useParams<{ obsId: string }>();
    const id = Number(obsId);
    const navigate = useNavigate();

    const [obs, setObs] = useState<SafetyObservation | null>(null);
    const [loading, setLoading] = useState(true);
    const [correctiveAction, setCorrectiveAction] = useState('');
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [fixedDate, setFixedDate] = useState(new Date().toISOString().slice(0, 10));
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [done, setDone] = useState(false);
    // Lightbox preview for before/after photos (full-resolution view)
    const [previewPhoto, setPreviewPhoto] = useState<PhotoMeta | null>(null);

    const fetchObs = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await getObservation(id);
            setObs(res.observation || null);
        } catch {
            // api util toasts
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchObs();
    }, [fetchObs]);

    // Evidence-of-fix picker — multiple files, uploaded immediately (the
    // observation already exists). Mirrors the Add Observation dropzone.
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (!obs) return;
        for (const file of acceptedFiles) {
            if (!['image/png', 'image/jpeg'].includes(file.type)) {
                return toast.error(`"${file.name}" — photo must be PNG or JPG`);
            }
            if (file.size > 10 * 1024 * 1024) {
                return toast.error(`"${file.name}" — photo exceeds maximum limit of 10MB`);
            }
        }
        setUploading(true);
        try {
            let latest: SafetyObservation = obs;
            for (const file of acceptedFiles) {
                const res = await uploadPhoto(id, 'afterPhoto', file);
                if (res.observation) latest = res.observation;
            }
            setObs(latest);
            toast.success(`${acceptedFiles.length} after photo(s) uploaded`);
        } catch (error: any) {
            toast.error(error.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    }, [id, obs]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] },
        maxSize: 10 * 1024 * 1024,
        multiple: true,
        disabled: uploading,
    });

    const afterPhotos: PhotoMeta[] =
        obs && Array.isArray(obs.afterPhoto) ? obs.afterPhoto : [];
    const beforePhotos: PhotoMeta[] =
        obs && Array.isArray(obs.beforePhoto) ? obs.beforePhoto : [];

    const deletePhoto = async (photoId: string) => {
        if (!obs) return;
        try {
            const res = await removePhoto(id, 'afterPhoto', photoId);
            setObs(res.observation);
            toast.success('After photo removed');
        } catch (error: any) {
            toast.error(error.message || 'Failed to remove photo');
        }
    };
    const submit = async () => {
        if (!correctiveAction.trim()) return toast.error('Corrective action description is required');
        if (afterPhotos.length === 0) return toast.error('Evidence of fix photo is required');
        setSubmitting(true);
        try {
            await submitCloseOut(id, {
                correctiveAction: correctiveAction.trim(),
                resolutionNotes: resolutionNotes.trim() || undefined,
                fixedDate: fixedDate || undefined,
            });
            setDone(true);
        } catch (error: any) {
            toast.error(error.message || 'Failed to submit close-out');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!obs) {
        return (
            <div className="max-w-[900px] mx-auto px-4 py-16 text-center text-muted-foreground">
                Observation not found.
            </div>
        );
    }

    // Success state (Miro: close-out success screen)
    if (done) {
        return (
            <div className="max-w-[600px] mx-auto px-4 py-16 text-center">
                <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-4" />
                <h1 className="text-2xl font-semibold mb-2">Close-Out Submitted</h1>
                <p className="text-muted-foreground mb-6">
                    {obs.obsNumber} has been submitted for review. The Safety Manager will verify the corrective action.
                </p>
                <button
                    onClick={() => navigate(`/safety/observations/${id}`)}
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                >
                    View Observation
                </button>
            </div>
        );
    }

    const inputCls = 'w-full h-10 px-3 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[900px] mx-auto w-full">
            <button onClick={() => navigate(`/safety/observations/${id}`)} className="text-sm text-primary hover:underline mb-4 inline-flex items-center gap-1">
                <ChevronLeft size={14} /> Back to Observation
            </button>
            <h1 className="text-2xl font-medium mb-6">Close Observation</h1>

            {/* Original observation */}
            <div className="rounded-xl border border-border bg-card p-5 mb-6">
                <h3 className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Original Observation</h3>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-primary">{obs.obsNumber}</span>
                    <RiskBadge risk={obs.riskRating} />
                    <DueIndicator isOverdue={obs.isOverdue} dueDate={obs.dueDate} />
                </div>
                <p className="text-sm font-medium">{obs.title || 'Untitled observation'}</p>
                {obs.description && <p className="text-sm text-muted-foreground mt-1">{obs.description}</p>}
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                    <span>Responsible: {obs.personResponsibleName || '—'}</span>
                    {obs.dueDate && <span>Due: {fmtDate(obs.dueDate)}</span>}
                </div>
                {beforePhotos.length > 0 && (
                    <div className="mt-3">
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Before Photos</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {beforePhotos.map((photo) => (
                                <PhotoThumb key={photo.id} photo={photo} onView={setPreviewPhoto} />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Corrective action */}
            <div className="rounded-xl border border-border bg-card p-5 mb-6">
                <h3 className="text-sm font-semibold mb-4">Corrective Action</h3>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                    CORRECTIVE ACTION DESCRIPTION <span className="text-red-500">*</span>
                </label>
                <textarea
                    value={correctiveAction}
                    onChange={(e) => setCorrectiveAction(e.target.value)}
                    placeholder="Describe the corrective action taken..."
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none mb-4"
                />
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">ADDITIONAL RESOLUTION NOTES</label>
                <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Add any additional resolution notes..."
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
            </div>

            {/* Evidence of fix */}
            <div className="rounded-xl border border-border bg-card p-5 mb-6">
                <h3 className="text-sm font-semibold mb-4">Evidence Of Fix</h3>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">AFTER PHOTO <span className="text-red-500">*</span></label>
                {afterPhotos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                        {afterPhotos.map((photo) => (
                            <div key={photo.id} className="rounded-lg border border-border overflow-hidden">
                                <PhotoThumb photo={photo} onView={setPreviewPhoto} className="border-0 rounded-none" />
                                <div className="flex items-center justify-between px-2 py-1.5 bg-muted/40 text-xs">
                                    <span className="text-muted-foreground truncate">{photo.filename}</span>
                                    <button onClick={() => deletePhoto(photo.id)} className="inline-flex items-center gap-1 text-red-600 hover:underline shrink-0">
                                        <X size={12} /> Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div
                    {...getRootProps()}
                    className={`w-full rounded-lg border-2 border-dashed border-border py-8 flex flex-col items-center justify-center text-muted-foreground cursor-pointer hover:bg-accent/40 ${
                        isDragActive ? 'border-primary bg-primary/5' : ''
                    } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                    <input {...getInputProps()} />
                    {uploading ? (
                        <Loader2 size={24} className="animate-spin mb-2" />
                    ) : (
                        <Upload size={24} className="mb-2" />
                    )}
                    <span className="text-sm">{afterPhotos.length > 0 ? 'Add More Photos' : 'Upload After Photo'}</span>
                    <span className="text-xs mt-1">PNG, JPG up to 10MB — drag &amp; drop or click to browse (multiple allowed)</span>
                </div>
            </div>

            {/* Completion details */}
            <div className="rounded-xl border border-border bg-card p-5 mb-6">
                <h3 className="text-sm font-semibold mb-4">Completion Details</h3>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">FIXED DATE</label>
                <input type="date" value={fixedDate} onChange={(e) => setFixedDate(e.target.value)} className={inputCls} />
            </div>

            {/* Auto-status notice */}
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-6">
                <ImageIcon size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                    <p>Status will automatically change to: <strong>Pending Verification</strong></p>
                    <p className="mt-0.5 text-xs">Responsible person and Safety Manager will be notified.</p>
                </div>
            </div>

            <div className="flex items-center justify-end gap-3">
                <button onClick={() => navigate(`/safety/observations/${id}`)} className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent">
                    Cancel
                </button>
                <button
                    onClick={submit}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                    {submitting && <Loader2 size={15} className="animate-spin" />}
                    Close Observation
                </button>
            </div>

            {/* Photo lightbox — full-resolution view */}
            <PhotoLightbox photo={previewPhoto} onClose={() => setPreviewPhoto(null)} />
        </div>
    );
};
